import { useEffect, useMemo, useRef, useState } from 'react';
import { logVideoDiagnostic, type VideoDiagnosticContext } from './videoDiagnostics';
import {
  analyzeMicrophoneSpeechFrame,
  createSpeechActivityDecisionState,
  updateSpeechActivityDecisionState,
  type SpeechActivityDecisionState,
  type SpeechActivityFrameAnalysis,
} from './microphoneSpeechActivity';

const SPEECH_ACTIVITY_ANALYSIS_INTERVAL_MS = 80;
const SPEECH_ACTIVITY_DIAGNOSTIC_INTERVAL_MS = 2000;
const SPEECH_ACTIVITY_CALIBRATION_SAMPLE_COUNT = 8;
const SPEECH_ACTIVITY_NOISE_FLOOR_MIN = 0.006;
const SPEECH_ACTIVITY_NOISE_FLOOR_MAX = 0.08;

export function useMicrophoneSpeechActivity(
  microphoneMediaTracks: MediaStreamTrack[],
  context: VideoDiagnosticContext = {}
) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const microphoneMediaTracksRef = useRef(microphoneMediaTracks);
  const trackSignature = useMemo(
    () => microphoneMediaTracks
      .map((track) => `${track.id}:${track.readyState}:${track.enabled ? 'enabled' : 'disabled'}`)
      .sort()
      .join('|'),
    [microphoneMediaTracks],
  );

  useEffect(() => {
    microphoneMediaTracksRef.current = microphoneMediaTracks;
  }, [microphoneMediaTracks]);

  useEffect(() => {
    const tracks = microphoneMediaTracksRef.current;

    logVideoDiagnostic('info', 'browser-audio-ducking-microphone-tracks', {
      trackCount: tracks.length,
      tracks: tracks.map((track) => ({
        id: track.id,
        label: track.label,
        readyState: track.readyState,
        enabled: track.enabled,
        muted: track.muted,
      })),
    }, context);

    if (tracks.length === 0) {
      setIsSpeaking(false);
      return;
    }

    const AudioContextCtor = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) {
      logVideoDiagnostic('warn', 'browser-audio-ducking-audio-context-unavailable', null, context);
      setIsSpeaking(false);
      return;
    }

    const audioContext = new AudioContextCtor();
    logVideoDiagnostic('info', 'browser-audio-ducking-audio-context-started', {
      state: audioContext.state,
      sampleRate: audioContext.sampleRate,
      trackCount: tracks.length,
    }, context);

    const monitors = tracks.map((track) => {
      const source = audioContext.createMediaStreamSource(new MediaStream([track]));
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.35;
      source.connect(analyser);

      return {
        track,
        source,
        analyser,
        samples: new Uint8Array(analyser.fftSize),
        frequencySamples: new Uint8Array(analyser.frequencyBinCount),
      };
    });
    let disposed = false;
    let speechActive = false;
    let decisionState = createSpeechActivityDecisionState();
    let calibrationSamples = 0;
    let noiseFloor = SPEECH_ACTIVITY_NOISE_FLOOR_MIN;
    let lastDiagnosticAt = 0;
    let lastAnalysis: SpeechActivityFrameAnalysis | null = null;
    let lastAudioContextState = audioContext.state;

    const resumeAudioContext = () => {
      if (audioContext.state === 'closed') return;

      audioContext.resume().catch((error) => {
        logVideoDiagnostic('warn', 'browser-audio-ducking-audio-context-resume-failed', { error }, context);
      });
    };

    const setSpeechDecisionState = (
      nextDecisionState: SpeechActivityDecisionState,
      analysis: SpeechActivityFrameAnalysis | null,
      track: MediaStreamTrack | null
    ) => {
      decisionState = nextDecisionState;
      if (speechActive === nextDecisionState.isSpeaking) return;

      speechActive = nextDecisionState.isSpeaking;
      logVideoDiagnostic('info', 'browser-audio-ducking-speaking-changed', {
        isSpeaking: nextDecisionState.isSpeaking,
        speechProbability: analysis?.speechProbability ?? null,
        rms: analysis?.rms ?? null,
        noiseFloor,
        dynamicRmsThreshold: analysis?.dynamicRmsThreshold ?? null,
        voiceBandRatio: analysis?.voiceBandRatio ?? null,
        midBandRatio: analysis?.midBandRatio ?? null,
        lowBandRatio: analysis?.lowBandRatio ?? null,
        highBandRatio: analysis?.highBandRatio ?? null,
        spectralCentroidHz: analysis?.spectralCentroidHz ?? null,
        zeroCrossingRate: analysis?.zeroCrossingRate ?? null,
        track: track ? summarizeDuckingMicrophoneTrack(track) : null,
        calibrationSamples,
      }, context);
      setIsSpeaking(nextDecisionState.isSpeaking);
    };

    const analyze = () => {
      if (disposed) return;

      let maxRms = 0;
      let hasLiveTrack = false;
      let bestAnalysis: SpeechActivityFrameAnalysis | null = null;
      let bestAnalysisTrack: MediaStreamTrack | null = null;

      for (const monitor of monitors) {
        if (monitor.track.readyState !== 'live' || !monitor.track.enabled) {
          continue;
        }

        hasLiveTrack = true;
        monitor.analyser.getByteTimeDomainData(monitor.samples);
        monitor.analyser.getByteFrequencyData(monitor.frequencySamples);

        const analysis = analyzeMicrophoneSpeechFrame({
          timeSamples: monitor.samples,
          frequencySamples: monitor.frequencySamples,
          sampleRate: audioContext.sampleRate,
          noiseFloor,
        });

        maxRms = Math.max(maxRms, analysis.rms);
        if (!bestAnalysis || analysis.speechProbability > bestAnalysis.speechProbability) {
          bestAnalysis = analysis;
          bestAnalysisTrack = monitor.track;
        }
      }

      if (!hasLiveTrack) {
        setSpeechDecisionState({
          isSpeaking: false,
          candidateStartedAt: null,
          lastSpeechAt: decisionState.lastSpeechAt,
        }, lastAnalysis, null);
        return;
      }

      const now = Date.now();
      if (audioContext.state !== lastAudioContextState) {
        const previousState = lastAudioContextState;
        lastAudioContextState = audioContext.state;

        if (audioContext.state === 'running') {
          calibrationSamples = 0;
          noiseFloor = SPEECH_ACTIVITY_NOISE_FLOOR_MIN;
        }

        logVideoDiagnostic('info', 'browser-audio-ducking-audio-context-state-changed', {
          from: previousState,
          to: audioContext.state,
          resetCalibration: audioContext.state === 'running',
        }, context);
      }

      if (calibrationSamples < SPEECH_ACTIVITY_CALIBRATION_SAMPLE_COUNT) {
        noiseFloor = clampMicrophoneSpeechNoiseFloor((noiseFloor * calibrationSamples + maxRms) / (calibrationSamples + 1));
        calibrationSamples += 1;
      } else if (!speechActive) {
        noiseFloor = clampMicrophoneSpeechNoiseFloor(noiseFloor * 0.96 + maxRms * 0.04);
      }

      if (!bestAnalysis) {
        setSpeechDecisionState({
          isSpeaking: false,
          candidateStartedAt: null,
          lastSpeechAt: decisionState.lastSpeechAt,
        }, lastAnalysis, null);
        return;
      }

      const nextDecisionState = updateSpeechActivityDecisionState({
        previousState: decisionState,
        analysis: bestAnalysis,
        now,
      });
      setSpeechDecisionState(nextDecisionState, bestAnalysis, bestAnalysisTrack);
      lastAnalysis = bestAnalysis;

      if (now - lastDiagnosticAt >= SPEECH_ACTIVITY_DIAGNOSTIC_INTERVAL_MS) {
        lastDiagnosticAt = now;
        logVideoDiagnostic('debug', 'browser-audio-ducking-level', {
          audioContextState: audioContext.state,
          rms: bestAnalysis.rms,
          speechProbability: bestAnalysis.speechProbability,
          noiseFloor,
          dynamicRmsThreshold: bestAnalysis.dynamicRmsThreshold,
          voiceBandRatio: bestAnalysis.voiceBandRatio,
          midBandRatio: bestAnalysis.midBandRatio,
          lowBandRatio: bestAnalysis.lowBandRatio,
          highBandRatio: bestAnalysis.highBandRatio,
          spectralCentroidHz: bestAnalysis.spectralCentroidHz,
          zeroCrossingRate: bestAnalysis.zeroCrossingRate,
          speechActive,
          calibrationSamples,
          hasLiveTrack,
          trackCount: monitors.length,
          bestTrack: bestAnalysisTrack ? summarizeDuckingMicrophoneTrack(bestAnalysisTrack) : null,
          candidateDurationMs: decisionState.candidateStartedAt === null ? 0 : now - decisionState.candidateStartedAt,
        }, context);
      }
    };

    resumeAudioContext();
    window.addEventListener('pointerdown', resumeAudioContext, { passive: true });
    window.addEventListener('touchend', resumeAudioContext, { passive: true });
    window.addEventListener('keydown', resumeAudioContext);
    analyze();
    const interval = window.setInterval(analyze, SPEECH_ACTIVITY_ANALYSIS_INTERVAL_MS);

    return () => {
      disposed = true;
      window.clearInterval(interval);
      window.removeEventListener('pointerdown', resumeAudioContext);
      window.removeEventListener('touchend', resumeAudioContext);
      window.removeEventListener('keydown', resumeAudioContext);
      monitors.forEach((monitor) => {
        monitor.source.disconnect();
        monitor.analyser.disconnect();
      });
      audioContext.close().catch(() => undefined);
    };
  }, [context, trackSignature]);

  return isSpeaking;
}

function clampMicrophoneSpeechNoiseFloor(value: number) {
  if (!Number.isFinite(value)) {
    return SPEECH_ACTIVITY_NOISE_FLOOR_MIN;
  }

  return Math.min(SPEECH_ACTIVITY_NOISE_FLOOR_MAX, Math.max(SPEECH_ACTIVITY_NOISE_FLOOR_MIN, value));
}

function summarizeDuckingMicrophoneTrack(track: MediaStreamTrack) {
  return {
    id: track.id,
    label: track.label,
    enabled: track.enabled,
    muted: track.muted,
    readyState: track.readyState,
  };
}
